import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Paper,
  Divider,
  InputAdornment,
  Avatar,
  Stack,
  Autocomplete,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Save,
  Cancel,
  Person,
  Business,
  Euro,
  CalendarToday,
  Description,
  CheckCircle,
  TrendingUp,
  ArrowForward,
  InfoOutlined,
  Image,
  AccountBalance,
  ReceiptLong,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import UserService from '../../services/userService';
import LimitService from '../../services/limitService';
import CreditService from '../../services/creditService';
import CheckImage from '../../components/credit/CheckImage';
import useResolvedUserId from '../../hooks/useResolvedUserId';

const normalizeSeller = (seller) => ({
  sellerId: seller.sellerId ?? seller.SellerId,
  sellerName: (seller.sellerName ?? seller.SellerName ?? '').trim(),
  email: seller.email ?? seller.Email,
});

const NewCredit = ({ user }) => {
  const navigate = useNavigate();
  const currentUserId = useResolvedUserId(user);
  const [formData, setFormData] = useState({
    buyerId: user?.userId?.toString() || '',
    buyerName: '',
    availableLimit: '',
    sellerId: '',
    sellerName: '',
    amount: '',
    currency: 'EUR',
    validityDate: null, // Changed to null for DatePicker
    description: '',
    buyerIban: '',
  });
  const [ibanWarning, setIbanWarning] = useState(false);
  const [loading, setLoading] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [previousSellers, setPreviousSellers] = useState([]);
  const [loadingSellers, setLoadingSellers] = useState(false);
  const [sellerInputValue, setSellerInputValue] = useState('');
  const [limitData, setLimitData] = useState(null); // Store raw limit data
  const [confirmDialog, setConfirmDialog] = useState({ open: false, proceed: false });

  const fetchPreviousSellers = useCallback(async (buyerUserId) => {
    if (!buyerUserId) return;
    setLoadingSellers(true);
    try {
      const result = await UserService.getActiveNonAdminUsers();
      if (result.success) {
        const sellers = (result.data || [])
          .map(normalizeSeller)
          .filter(
            (seller) =>
              seller.sellerId != null &&
              String(seller.sellerId) !== String(buyerUserId) &&
              seller.sellerName
          );
        setPreviousSellers(sellers);
      }
    } catch (error) {
      console.error('Error fetching active sellers:', error);
    } finally {
      setLoadingSellers(false);
    }
  }, []);

  const initializeBuyerData = useCallback(async () => {
    if (!user) return;

    try {
      setProfileLoading(true);
      const result = await UserService.getCurrentUserProfile();
      if (!result.success || !result.data) {
        setFormData((prev) => ({ ...prev, buyerIban: '' }));
        setIbanWarning(true);
        return;
      }

      const profile = result.data.data || result.data;
      const buyerUserId = profile.userId ?? currentUserId;
      if (!buyerUserId) {
        return;
      }

      const buyerName =
        profile.name && profile.surname
          ? `${profile.name} ${profile.surname}`
          : user.name && user.surname
            ? `${user.name} ${user.surname}`
            : user.name || '';

      setFormData((prev) => ({
        ...prev,
        buyerId: buyerUserId.toString(),
        buyerName,
        buyerIban: profile.ibanNumber ? formatIban(profile.ibanNumber) : '',
      }));
      setIbanWarning(!profile.ibanNumber);

      fetchAvailableLimit(buyerUserId);
      fetchPreviousSellers(buyerUserId);
    } catch (error) {
      console.error('Error initializing buyer data:', error);
      setFormData((prev) => ({ ...prev, buyerIban: '' }));
      setIbanWarning(true);
    } finally {
      setProfileLoading(false);
    }
  }, [user, currentUserId, fetchPreviousSellers]);

  // Initialize buyer info and seller list (profile provides numeric userId for Fidost auth)
  useEffect(() => {
    initializeBuyerData();
  }, [initializeBuyerData]);

  // Sync sellerInputValue with formData.sellerId when formData changes externally
  useEffect(() => {
    if (formData.sellerId) {
      // If sellerId exists, update inputValue to show it
      // Check if it's in previousSellers, if so show the formatted label, otherwise just the ID
      const sellerFromList = previousSellers.find(s => s.sellerId.toString() === formData.sellerId);
      if (sellerFromList) {
        // If in list, show ID (as we do in handleSellerChange)
        setSellerInputValue(formData.sellerId);
      } else if (!sellerInputValue || sellerInputValue !== formData.sellerId) {
        // If not in list but sellerId exists, show the ID
        setSellerInputValue(formData.sellerId);
      }
    } else if (!formData.sellerId && sellerInputValue) {
      // If sellerId is cleared, clear inputValue too
      setSellerInputValue('');
    }
  }, [formData.sellerId, previousSellers]);


  // Get available limit
  const fetchAvailableLimit = async (userId) => {
    if (!userId) return;
    const result = await LimitService.getLimitByBuyerId(userId);
    if (result.success && result.data) {
      setLimitData(result.data); // Store raw limit data
      setFormData(prev => ({
        ...prev,
        availableLimit: formatCurrency(result.data.remainingAmount, result.data.currency)
      }));
    } else {
      setLimitData(null); // No limit available
      setFormData(prev => ({ ...prev, availableLimit: 'N/A' }));
    }
  };

  // Get seller name by ID
  const fetchSellerName = async (sellerId) => {
    if (!sellerId) {
      setFormData(prev => ({ ...prev, sellerName: '' }));
      return;
    }

    // Check if user is trying to enter their own ID - just show warning, don't block
    if (sellerId === formData.buyerId) {
      setError('You cannot create a payment for yourself. Please enter a different seller ID.');
      setFormData(prev => ({ ...prev, sellerName: '' }));
      return;
    }

    // Clear previous error if not own ID
    setError('');

    const result = await UserService.getCustomerNameByUserId(sellerId);
    if (result.success) {
      setFormData(prev => ({ ...prev, sellerName: result.data.customerName }));
    } else {
      setFormData(prev => ({ ...prev, sellerName: '' }));
      setError(result.message);
    }
  };

  // Handle seller selection from autocomplete
  const handleSellerChange = (event, newValue, reason) => {
    if (reason === 'selectOption' && newValue && typeof newValue === 'object') {
      const seller = normalizeSeller(newValue);
      if (!seller.sellerId) return;

      // Selected from dropdown - set both sellerId and sellerName immediately
      const sellerIdStr = seller.sellerId.toString();
      setFormData(prev => ({
        ...prev,
        sellerId: sellerIdStr,
        sellerName: seller.sellerName
      }));
      // Set inputValue to sellerId (not the formatted label)
      // This will be set after onInputChange completes
      setSellerInputValue(sellerIdStr);
      setError('');
    } else if (reason === 'clear') {
      // Cleared
      setFormData(prev => ({
        ...prev,
        sellerId: '',
        sellerName: ''
      }));
      setSellerInputValue('');
      setError('');
    }
  };

  // Debounce seller ID fetch when user types
  useEffect(() => {
    if (!sellerInputValue || sellerInputValue.trim() === '') {
      // Only clear if sellerId is also empty (don't clear when user selected from dropdown)
      setFormData(prev => {
        if (!prev.sellerId) {
          return { ...prev, sellerId: '', sellerName: '' };
        }
        return prev; // Keep existing sellerId and sellerName if they exist
      });
      return;
    }

    // If it's a number (likely ID)
    if (/^\d+$/.test(sellerInputValue.trim())) {
      const trimmedValue = sellerInputValue.trim();
      
      // Check if this ID is in previousSellers
      const sellerFromList = previousSellers.find(s => s.sellerId.toString() === trimmedValue);
      
      if (sellerFromList) {
        // If in previousSellers, always set sellerName (even if already set, to ensure it's correct)
        setFormData(prev => ({
          ...prev,
          sellerId: trimmedValue,
          sellerName: sellerFromList.sellerName
        }));
        // Don't fetch from API if it's in previousSellers
        return;
      }
      
      // Not in previousSellers, update sellerId and fetch from API
      setFormData(prev => ({ ...prev, sellerId: trimmedValue }));
      const timeoutId = setTimeout(() => {
        fetchSellerName(trimmedValue);
      }, 500);
      return () => clearTimeout(timeoutId);
    } else {
      // If it's text, try to find in previous sellers
      const found = previousSellers.find(s => 
        s.sellerName.toLowerCase() === sellerInputValue.toLowerCase()
      );
      if (found) {
        setFormData(prev => ({
          ...prev,
          sellerId: found.sellerId.toString(),
          sellerName: found.sellerName
        }));
        setError('');
      } else {
        setFormData(prev => ({ ...prev, sellerId: sellerInputValue, sellerName: '' }));
      }
    }
  }, [sellerInputValue, previousSellers]);

  // Format IBAN: remove spaces, allow alphanumeric, uppercase, limit length, add space every 4 chars
  const formatIban = (iban) => {
    if (!iban) return '';
    const cleanIban = iban
      .replace(/\s+/g, '')
      .replace(/[^0-9A-Za-z]/g, '')
      .toUpperCase()
      .slice(0, 32);
    return cleanIban.replace(/(.{4})/g, '$1 ').trim();
  };

  const handleInputChange = (field, value) => {
    // Special handling for IBAN - apply mask with TR prefix
    if (field === 'buyerIban') {
      const formattedValue = formatIban(value);
      setFormData(prev => ({ ...prev, [field]: formattedValue }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
    if (error) setError('');
    if (success) setSuccess('');
  };

  const formatCurrency = (amount, currency = 'EUR') => {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    const parts = formatter.formatToParts(amount);
    let formatted = '';
    for (const part of parts) {
      if (part.type === 'currency') {
        formatted += part.value + ' ';
      } else {
        formatted += part.value;
      }
    }
    return formatted.trim();
  };

  // Modified formatCurrencyInput function:
const formatCurrencyInput = (value) => {
  // Remove all non-numeric characters except decimal point
  const numericValue = value.replace(/[^\d.]/g, '');
  
  // Ensure only one decimal point
  const parts = numericValue.split('.');
  if (parts.length > 2) {
    return parts[0] + '.' + parts.slice(1).join('');
  }
  
  // Limit decimal places to 2
  if (parts[1] && parts[1].length > 2) {
    parts[1] = parts[1].substring(0, 2);
  }
  
  return parts.join('.');
};

  const handleAmountChange = (value) => {
    const formatted = formatCurrencyInput(value);
    handleInputChange('amount', formatted);
  };

  // Get raw amount value (remove commas for API)
  const getRawAmount = () => {
    return formData.amount.replace(/,/g, '');
  };

  const validateForm = () => {
    if (!formData.buyerId) {
      setError('Buyer ID is required');
      return false;
    }
    if (!formData.sellerId) {
      setError('Seller ID is required');
      return false;
    }
    // Check if user is trying to create payment for themselves
    if (formData.sellerId === formData.buyerId) {
      setError('You cannot create a payment for yourself. Please enter a different seller ID.');
      return false;
    }
    if (!formData.sellerName) {
      setError('Please enter a valid Seller ID');
      return false;
    }
    if (!formData.amount || parseFloat(getRawAmount()) <= 0) {
      setError('Amount must be greater than zero');
      return false;
    }
    if (!formData.validityDate) {
      setError('Payment date is required');
      return false;
    }
    const selectedDate = new Date(formData.validityDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (selectedDate <= today) {
      setError('Payment date must be a future date');
      return false;
    }
    // Validate IBAN (remove spaces for validation)
    const cleanIban = (formData.buyerIban || '').replace(/\s/g, '').trim().toUpperCase();
    if (!cleanIban) {
      setError('Please add your IBAN from the Profile page before creating a payment.');
      return false;
    }
    if (cleanIban.length > 32) {
      setError('IBAN number cannot exceed 32 characters.');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('[NewCredit] handleSubmit called');
    
    if (ibanWarning) {
      setError('Please add your IBAN from the Profile page before creating a payment.');
      return;
    }

    if (!validateForm()) {
      console.log('[NewCredit] Form validation failed');
      return;
    }

    // Check if amount exceeds available limit or no limit exists
    const enteredAmount = parseFloat(getRawAmount());
    const hasLimit = limitData && limitData.remainingAmount !== undefined && limitData.remainingAmount !== null;
    
    // Check if currencies match (if limit exists)
    let sameCurrency = true;
    if (hasLimit && limitData.currency && formData.currency) {
      const limitCurrency = typeof limitData.currency === 'string' 
        ? limitData.currency.toUpperCase() 
        : limitData.currency.toString();
      const formCurrency = formData.currency.toUpperCase();
      sameCurrency = limitCurrency === formCurrency;
    }
    
    const exceedsLimit = hasLimit && sameCurrency && enteredAmount > limitData.remainingAmount;
    const noLimit = !hasLimit || limitData.remainingAmount === null || limitData.remainingAmount === undefined;
    const differentCurrency = hasLimit && !sameCurrency;

    if (exceedsLimit || noLimit || differentCurrency) {
      // Show confirmation dialog
      setConfirmDialog({ open: true, proceed: false });
      return;
    }

    // Proceed with submission if limit is sufficient
    await proceedWithSubmission();
  };

  const proceedWithSubmission = async () => {
    console.log('[NewCredit] proceedWithSubmission called');
    setLoading(true);
    setError('');
    setSuccess('');
    setConfirmDialog({ open: false, proceed: false });

    try {
      const currencyMap = {
        'TRY': 1,
        'USD': 2,
        'EUR': 3,
        'GBP': 4
      };

      // Format date to YYYY-MM-DD for API (use local date, not UTC to avoid timezone issues)
      let formattedDate = null;
      if (formData.validityDate) {
        if (formData.validityDate instanceof Date) {
          // Use local date components to avoid timezone conversion issues
          const year = formData.validityDate.getFullYear();
          const month = String(formData.validityDate.getMonth() + 1).padStart(2, '0');
          const day = String(formData.validityDate.getDate()).padStart(2, '0');
          formattedDate = `${year}-${month}-${day}`;
        } else if (typeof formData.validityDate === 'string') {
          // If it's already a string, try to parse and format it
          const dateObj = new Date(formData.validityDate);
          if (!isNaN(dateObj.getTime())) {
            const year = dateObj.getFullYear();
            const month = String(dateObj.getMonth() + 1).padStart(2, '0');
            const day = String(dateObj.getDate()).padStart(2, '0');
            formattedDate = `${year}-${month}-${day}`;
          } else {
            formattedDate = formData.validityDate; // Use as is if parsing fails
          }
        } else {
          formattedDate = formData.validityDate;
        }
      }

      console.log('[NewCredit] Date formatting - original:', formData.validityDate, 'formatted:', formattedDate);

      const creditData = {
        sellerId: parseInt(formData.sellerId),
        sellerName: formData.sellerName,
        amount: parseFloat(getRawAmount()),
        currency: currencyMap[formData.currency] || 1,
        validityDate: formattedDate,
        description: formData.description,
        buyerIban: formData.buyerIban.replace(/\s/g, '').trim().toUpperCase() // Remove spaces before sending
      };

      console.log('[NewCredit] Credit data prepared:', {
        ...creditData,
        amount: creditData.amount,
        currency: creditData.currency,
        formattedDate: formattedDate
      });

      const result = await CreditService.createCreditRequest(creditData);
      
      console.log('[NewCredit] API response received:', result);
      
      if (result.success) {
        const paymentNumber = result.data?.paymentNumber;
        const isGuaranteed = result.data?.isGuaranteed || false;
        const paymentType = isGuaranteed ? 'Payment Guaranteed' : 'Payment';
        const successMessage = paymentNumber 
          ? `${result.message} ${paymentType} Number: ${paymentNumber}`
          : result.message;
        console.log('[NewCredit] Success:', successMessage);
        setSuccess(successMessage);
        setTimeout(() => {
          navigate('/user/credits');
        }, 2000);
      } else {
        console.error('[NewCredit] API returned error:', result);
        // Show detailed error message
        let errorMessage = result.message || 'An error occurred while creating the payment request.';
        if (result.error) {
          errorMessage += `\n\nDetails: ${result.error}`;
        }
        if (result.innerError && result.innerError.includes('sp_GeneratePaymentNumber')) {
          errorMessage += '\n\nPlease contact the administrator to run the SQL script for payment number generation.';
        }
        setError(errorMessage);
      }
    } catch (error) {
      console.error('[NewCredit] Exception in handleSubmit:', error);
      console.error('[NewCredit] Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      setError('An error occurred while creating the payment request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/user/credits');
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      py: 4,
      px: { xs: 2, sm: 3 }
    }}>
      <Box sx={{ maxWidth: 900, mx: 'auto' }}>
        {/* Main Card */}
        <Card elevation={24} sx={{ 
          borderRadius: 4,
          overflow: 'hidden'
        }}>
          
          {/* Buyer Info Banner */}
          <Box sx={{ 
            background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
            p: 3,
            color: 'white'
          }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <Avatar sx={{ 
                  width: 64, 
                  height: 64,
                  background: 'rgba(255,255,255,0.25)',
                  border: '3px solid rgba(255,255,255,0.3)',
                  fontSize: '1.5rem'
                }}>
                  {user?.name && user?.surname ? 
                    `${user.name.charAt(0)}${user.surname.charAt(0)}`.toUpperCase() : 
                    user?.name ? user.name.charAt(0).toUpperCase() : 'U'}
                </Avatar>
              </Grid>
              <Grid item xs>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {formData.buyerName}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Customer ID: {formData.buyerId}
                </Typography>
              </Grid>
              <Grid item xs={12} sm="auto">
                <Box sx={{ textAlign: { xs: 'center', sm: 'right' } }}>
                  <Typography variant="caption" sx={{ opacity: 0.9, display: 'block' }}>
                    Available Limit
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {formData.availableLimit}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.7, fontSize: '0.7rem', mt: 0.5 }}>
                    {formData.availableLimit !== 'N/A' ? 'Guaranteed payments available' : 'Non-guaranteed payments only'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  startIcon={<ReceiptLong />}
                  onClick={() => navigate('/user/invoice-approval')}
                  sx={{
                    background: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    '&:hover': {
                      background: 'rgba(255,255,255,0.3)',
                    }
                  }}
                >
                  Upload New Invoice
                </Button>
              </Grid>
            </Grid>
          </Box>

          {/* Form */}
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', mb: 1 }}>
                Create Payment Guarantee
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Fill in the seller and guarantee details below.
              </Typography>
            </Box>
            <Box component="form" onSubmit={handleSubmit}>
              
              {/* Seller Information */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ 
                  mb: 3, 
                  display: 'flex', 
                  alignItems: 'center',
                  fontWeight: 600,
                  color: 'text.primary'
                }}>
                  <Business sx={{ mr: 1.5, color: '#1976d2' }} />
                  Seller Information
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      freeSolo
                      options={previousSellers}
                      fullWidth
                      getOptionLabel={(option) => {
                        if (!option) return '';
                        if (typeof option === 'string') return option;
                        return `${option.sellerName} (ID: ${option.sellerId})`;
                      }}
                      isOptionEqualToValue={(option, value) => {
                        if (typeof option === 'string' || typeof value === 'string') return false;
                        return option.sellerId === value.sellerId;
                      }}
                      value={
                        formData.sellerId 
                          ? (previousSellers.find(s => s.sellerId.toString() === formData.sellerId) || null)
                          : null
                      }
                      inputValue={sellerInputValue}
                      onChange={handleSellerChange}
                      onInputChange={(event, newInputValue, reason) => {
                        if (reason === 'input') {
                          setSellerInputValue(newInputValue);
                        } else if (reason === 'reset' || reason === 'clear') {
                          setSellerInputValue('');
                        }
                      }}
                      loading={loadingSellers}
                      sx={{ 
                        width: '100%',
                        display: 'block',
                        '& .MuiAutocomplete-root': {
                          width: '100%'
                        },
                        '& .MuiAutocomplete-inputRoot': {
                          width: '100% !important',
                          minWidth: '100% !important',
                          flexWrap: 'nowrap'
                        },
                        '& .MuiAutocomplete-input': {
                          width: '100% !important',
                          minWidth: 0,
                          flex: '1 1 auto'
                        }
                      }}
                      filterOptions={(options, { inputValue }) => {
                        if (!inputValue) return options;
                        const searchTerm = inputValue.toLowerCase();
                        return options.filter(option =>
                          option.sellerName.toLowerCase().includes(searchTerm) ||
                          option.sellerId.toString().includes(searchTerm)
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Seller ID or Name"
                          placeholder="Enter seller ID or select from previous sellers"
                          required
                          fullWidth
                          error={
                            (formData.sellerId === formData.buyerId) && 
                            formData.sellerId !== ''
                          }
                          helperText={
                            (formData.sellerId === formData.buyerId) && 
                            formData.sellerId !== ''
                              ? 'You cannot create a payment for yourself'
                              : ''
                          }
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <InputAdornment position="start">
                                  <Business color={formData.sellerId ? 'primary' : 'disabled'} />
                                </InputAdornment>
                                {params.InputProps.startAdornment}
                              </>
                            ),
                            endAdornment: (
                              <>
                                {loadingSellers ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                          sx={{
                            width: '100%',
                            '& .MuiOutlinedInput-root': {
                              '&:hover fieldset': {
                                borderColor: '#1976d2',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: '#1976d2',
                              }
                            }
                          }}
                        />
                      )}
                      renderOption={(props, option) => {
                        const { key, ...otherProps } = props;
                        return (
                          <Box component="li" key={key} {...otherProps}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                              <Business color="primary" fontSize="small" />
                              <Typography variant="body1" fontWeight={600}>
                                {option.sellerName}
                              </Typography>
                            </Box>
                          </Box>
                        );
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Seller Name"
                      value={formData.sellerName || ''}
                      variant="outlined"
                      InputProps={{
                        readOnly: true,
                        startAdornment: (
                          <InputAdornment position="start">
                            <Business color={formData.sellerName ? 'success' : 'disabled'} />
                          </InputAdornment>
                        ),
                        endAdornment: formData.sellerName && (
                          <InputAdornment position="end">
                            <CheckCircle color="success" />
                          </InputAdornment>
                        )
                      }}
                      sx={{ 
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: formData.sellerName ? 'rgba(76, 175, 80, 0.08)' : 'grey.50'
                        }
                      }}
                    />
                  </Grid>

                  {formData.sellerName && (
                    <Grid item xs={12}>
                      <Alert 
                        severity="success" 
                        icon={<CheckCircle />}
                        sx={{ 
                          borderLeft: '4px solid',
                          borderColor: 'success.main'
                        }}
                      >
                        <strong>{formData.sellerName}</strong> is a verified seller on DPG Finance
                      </Alert>
                    </Grid>
                  )}
                </Grid>
              </Box>

              <Divider sx={{ my: 4 }} />

              {/* Credit Details */}
              <Box sx={{ mb: 4 }}>
  <Typography variant="h6" sx={{ 
    mb: 3, 
    display: 'flex', 
    alignItems: 'center',
    fontWeight: 600,
    color: 'text.primary'
  }}>
    <Euro sx={{ mr: 1.5, color: '#1976d2' }} />
    Payment Guarantee Details
  </Typography>

              <Grid container spacing={3}>
    <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Amount"
                      value={formData.amount}
                      onChange={(e) => handleAmountChange(e.target.value)}
                      variant="outlined"
                      required
                      type="text"
                      placeholder="0.00"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Typography variant="body1" sx={{ fontWeight: 600, color: formData.amount ? '#1976d2' : 'text.disabled' }}>
                              EUR
                            </Typography>
                          </InputAdornment>
                        )
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: '#1976d2',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#1976d2',
                          }
                        },
                        '& input': {
                          fontSize: '1.1rem',
                          fontWeight: 500
                        }
                      }}
                    />
                  </Grid>

                   <Grid item xs={12} sm={6}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        label="Payment Date"
                        value={formData.validityDate}
                        onChange={(newValue) => handleInputChange('validityDate', newValue)}
                        minDate={new Date(new Date().setDate(new Date().getDate() + 1))}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            required: true,
                            variant: 'outlined',
                            InputProps: {
                              startAdornment: (
                                <InputAdornment position="start">
                                  <CalendarToday color={formData.validityDate ? 'primary' : 'disabled'} />
                                </InputAdornment>
                              ),
                            },
                            sx: {
                              '& .MuiOutlinedInput-root': {
                                '&:hover fieldset': {
                                  borderColor: '#1976d2',
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: '#1976d2',
                                },
                              }
                            }
                          },
                          popper: {
                            sx: {
                              '& .MuiPaper-root': {
                                borderRadius: 3,
                                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                              },
                              '& .MuiPickersDay-root': {
                                borderRadius: 3,
                                '&.Mui-selected': {
                                  backgroundColor: '#1976d2',
                                  '&:hover': {
                                    backgroundColor: '#1565c0',
                                  }
                                }
                              },
                              '& .MuiPickersCalendarHeader-root': {
                                '& .MuiPickersCalendarHeader-switchViewButton': {
                                  color: '#1976d2',
                                },
                                '& .MuiPickersArrowSwitcher-button': {
                                  color: '#1976d2',
                                }
                              }
                            }
                          }
                        }}
                      />
                    </LocalizationProvider>
                  </Grid>                                
                </Grid>
                <Box sx={{ mt: 3 }}>
                  <TextField
                    fullWidth
                    label="Buyer IBAN"
                    value={formData.buyerIban}
                    variant="outlined"
                    required
                    disabled
                    placeholder="TR44 4444 4444 4444 4444 4444 44"
                    helperText={ibanWarning ? 'Please add your IBAN from the Profile page before creating a payment.' : 'Manage your IBAN from the Profile page.'}
                    error={ibanWarning}
                    inputProps={{
                      style: { textTransform: 'uppercase' }
                    }}
                    InputProps={{
                      readOnly: true,
                      startAdornment: (
                        <InputAdornment position="start">
                          <AccountBalance color="primary" />
                        </InputAdornment>
                      )
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: '#1976d2',
                        },
                        '&.Mui-disabled fieldset': {
                          borderColor: '#d0d7de',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#1976d2',
                        }
                      }
                    }}
                  />
                </Box>
                <Box sx={{ mt: 3 }}>
    <TextField
      fullWidth
      label="Description (Optional)"
      value={formData.description}
      onChange={(e) => handleInputChange('description', e.target.value)}
      variant="outlined"
      multiline
      rows={3}
      placeholder="Add any additional information about this payment guarantee request..."
      InputProps={{
        startAdornment: (
          <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5 }}>
            <Description color="primary" />
          </InputAdornment>
        )
      }}
      sx={{
        '& .MuiOutlinedInput-root': {
          '&:hover fieldset': {
            borderColor: '#1976d2',
          },
          '&.Mui-focused fieldset': {
            borderColor: '#1976d2',
          }
        }
      }}
    />
  </Box>
                <Box sx={{ mt: 3 }}>
                   {/* Check Preview - Otomatik Oluşturulan Çek */}
                      <Box
                        sx={{
                          border: '1px solid #e0e0e0',
                          borderRadius: 3,
                          p: 2,
                          backgroundColor: '#fafafa',
                        }}
                      >
                        <CheckImage
                          checkData={{
                            amount: parseFloat(getRawAmount()) || 0,
                            currency: formData.currency,
                            buyerName: formData.buyerName || 'Buyer Name',
                            sellerName: formData.sellerName || 'Seller Name',
                            buyerIban: formData.buyerIban || 'IBAN not available',
                            dueDate: formData.validityDate,
                            checkNumber: '',
                            paymentNumber: '',
                            isGuaranteed: false, // Preview'da limit kontrolü yapılmadığı için false gösteriyoruz
                            description: formData.description,
                            creatorName: user?.name && user?.surname ? `${user.name} ${user.surname}` : user?.name || '',
                          }}
                          preview={true}
                          showTemplate={true}
                        />
                      </Box>
                </Box>
             
</Box>

              {/* Info Banner */}
              <Paper sx={{ 
                p: 2.5, 
                mb: 4, 
                backgroundColor: 'rgba(102, 126, 234, 0.08)',
                border: '1px solid rgba(102, 126, 234, 0.2)',
                borderRadius: 3
              }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <InfoOutlined sx={{ color: '#1976d2', mt: 0.5 }} />
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: '#1976d2' }}>
                      Important Information
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • If you have sufficient limit, payment will be guaranteed by the bank<br/>
                      • If limit is insufficient or unavailable, payment will be created without guarantee<br/>
                      • Payment date must be a future date<br/>
                      • All sellers are verified for secure transactions
                    </Typography>
                  </Box>
                </Box>
              </Paper>

              {ibanWarning && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                  Please add your IBAN from the Profile page before creating a payment. Once saved, reopen this page to continue.
                </Alert>
              )}

              {/* Error/Success Messages */}
              {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                  {error}
                </Alert>
              )}

              {success && (
                <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
                  {success}
                </Alert>
              )}

              {/* Action Buttons */}
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  onClick={handleCancel}
                  disabled={loading || profileLoading}
                  startIcon={<Cancel />}
                  size="large"
                  sx={{
                    borderColor: '#1976d2',
                    color: '#1976d2',
                    '&:hover': {
                      borderColor: '#764ba2',
                      backgroundColor: 'rgba(102, 126, 234, 0.04)'
                    }
                  }}
                >
                  Cancel
                </Button>

                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading || ibanWarning || profileLoading || !formData.buyerIban}
                  endIcon={loading ? <CircularProgress size={20} color="inherit" /> : <ArrowForward />}
                  size="large"
                  sx={{ 
                    minWidth: 180,
                    background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                    boxShadow: '0 4px 15px rgba(25, 118, 210, 0.4)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                      boxShadow: '0 6px 20px rgba(25, 118, 210, 0.6)',
                    }
                  }}
                >
                  {loading ? 'Sending...' : 'Send to Seller'}
                </Button>
              </Stack>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Confirmation Dialog for Non-Guaranteed Payment */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, proceed: false })}
        PaperProps={{
          sx: {
            borderRadius: 3,
            minWidth: 400,
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          color: 'warning.main'
        }}>
          <InfoOutlined />
          Non-Guaranteed Payment
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '1rem', lineHeight: 1.6 }}>
            The payment amount exceeds your available limit or you do not have an active limit.
            <br /><br />
            <strong>This transaction will proceed without guarantee.</strong>
            <br /><br />
            Do you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setConfirmDialog({ open: false, proceed: false })}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={async () => {
              setConfirmDialog({ open: false, proceed: true });
              await proceedWithSubmission();
            }}
            variant="contained"
            color="warning"
            sx={{ 
              borderRadius: 2,
              background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #f57c00 0%, #e65100 100%)',
              }
            }}
          >
            Continue Without Guarantee
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NewCredit;